/**
 * The critical-alert cue: a short generated two-tone chime through the Web
 * Audio API — no audio asset to load, cache or license. The audio context is
 * injected (`createContext`), so the unit test drives a recording fake and a
 * page without Web Audio (or before the operator's first click, when the
 * browser keeps a context suspended) simply stays quiet.
 */

/** The parts of the Web Audio API the cue uses. */
export type ToneContext = {
  readonly currentTime: number;
  readonly state: string;
  readonly destination: unknown;
  resume(): Promise<void>;
  createOscillator(): {
    type: string;
    readonly frequency: { setValueAtTime(value: number, at: number): unknown };
    connect(node: unknown): unknown;
    start(at: number): void;
    stop(at: number): void;
  };
  createGain(): {
    readonly gain: { setValueAtTime(value: number, at: number): unknown; exponentialRampToValueAtTime(value: number, at: number): unknown };
    connect(node: unknown): unknown;
  };
};

/** The chime: two notes (a falling fifth, A5 → D5), 180 ms each, a soft attack and decay. */
export const CUE_NOTES: readonly { readonly hz: number; readonly at: number; readonly length: number }[] = [
  { hz: 880, at: 0, length: 0.18 },
  { hz: 587.33, at: 0.2, length: 0.18 },
];

/** How loud (0–1): audible over a gallery's programme sound without startling. */
const PEAK_GAIN = 0.25;

/** Play the cue once on `context`. Never throws: a cue that cannot play is silence, not a broken screen. */
export function playCue(context: ToneContext | null): boolean {
  if (context === null) return false;
  try {
    if (context.state === 'suspended') void context.resume().catch(() => undefined);
    const t0 = context.currentTime;
    for (const note of CUE_NOTES) {
      const osc = context.createOscillator();
      const gain = context.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(note.hz, t0 + note.at);
      gain.gain.setValueAtTime(0.0001, t0 + note.at);
      gain.gain.exponentialRampToValueAtTime(PEAK_GAIN, t0 + note.at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + note.at + note.length);
      osc.connect(gain);
      gain.connect(context.destination);
      osc.start(t0 + note.at);
      osc.stop(t0 + note.at + note.length + 0.02);
    }
    return true;
  } catch {
    return false;
  }
}
